import { Component, Input, OnChanges, ViewChild } from "@angular/core";
import { APIResults } from "src/app/sharedinterfaces";
import { ApiService } from "src/app/api.service";
import { ChartingService } from "src/app/charting.service";
import { DvsChartComponent } from "src/app/shared/dvs-chart/dvs-chart.component";
import { DvsUtilities } from "src/app/dvs-utilities";

@Component({
	selector: "app-adjustment-history",
	templateUrl: "./adjustment-history.component.html",
	styleUrls: ["./adjustment-history.component.scss"]
})
export class AdjustmentHistoryComponent implements OnChanges {
	@Input()
	asset: APIResults.FleetAsset;
	@Input()
	fleet: APIResults.Fleet;
	@Input()
	rotor: APIResults.Rotor;
	@Input()
	archive: AdjustmentArchive;
	@Input()
	regimeConfigs: APIResults.RegimeIndex;

	@ViewChild("polarChart", { static: true })
	polarChart: DvsChartComponent;
	@ViewChild("timelineChart", { static: true })
	timelineChart: DvsChartComponent;

	public regimeCounts: APIResults.RegimeCount[] = [];
	public selectedRegimes: number[];
	public vshafts?: APIResults.VShaft[];
	public selectedVShaft: APIResults.VShaft;
	public shaftOrder = 1;

	/** Keeps unfiltered polar points to filter when zooming */
	public allPolarPoints: any[][] = [];
	public polarOptions: Highcharts.Options;
	public timelineOptions: Highcharts.Options;
	// Keep last known min/max so we can maintain timeline zoom when reloading
	public timelineMin: number;
	public timelineMax: number;

	public userNote: string;
	public adjustments: APIResults.AdjustmentHistory[];
	public blades: string;

	constructor(public api: ApiService, private charting: ChartingService) { }

	async ngOnChanges() {
		this.vshafts = this.rotor.vshaft;
		const findDefault = this.vshafts.find(item => item.default);
		if (findDefault) {
			this.selectedVShaft = findDefault;
		} else {
			this.selectedVShaft = this.vshafts[0];
		}

		await this.updateAdjustments();
		await this.loadRegimes();
	}

	// When first loaded or upstream params (sensor, order) change
	async loadRegimes() {
		const newCounts = await this.api.GetRTBHistoryCounts(this.asset.asset_id, this.rotor.rotorid, this.selectedVShaft.shftid, this.shaftOrder);
		if (this.selectedRegimes) {
			// Carry over current selection
			for (const recheck of this.selectedRegimes) {
				const sameRegime = newCounts.find(item => item.regime_id === recheck);
				if (sameRegime) {
					sameRegime.evaluate = true;
				}
			}
		} else {
			// Populate selection with default regimes
			let evalempty = true;
			for (const regime of newCounts) {
				regime.evaluate = this.regimeConfigs[regime.regime_id].default;
				if (regime.evaluate) {
					evalempty = false;
				}
			}
			// If no default regimes available, select last one
			if (evalempty && newCounts.length) {
				newCounts[newCounts.length - 1].evaluate = true;
			}
		}
		this.regimeCounts = newCounts;
		await this.regimesChange();
	}

	async regimesChange() {
		this.selectedRegimes = this.regimeCounts.filter(item => item.evaluate).map(item => item.regime_id);
		await this.updatePlots();
	}

	async updatePlots() {
		if (!this.selectedRegimes || !this.selectedRegimes.length) {
			return;
		}
		this.polarOptions = undefined;
		this.timelineOptions = undefined;
		const results = await this.api.GetRTBHistory(this.asset.asset_id, this.rotor.rotorid, this.selectedVShaft.shftid, this.selectedRegimes, this.shaftOrder);
		const polarSeries: Highcharts.SeriesScatterOptions[] = [];
		const timelineSeries: Highcharts.SeriesLineOptions[] = [];
		this.allPolarPoints = [];
		let regimeIndex = 0;
		for (const regimeData of results.regimes) {
			const thisRegime = regimeIndex;
			let regimeLookup = this.regimeCounts.find(item => item.regime_id === regimeData.regime);
			if (!regimeLookup) regimeLookup = { name: "Unknown regime " + regimeData.regime, regime_id: regimeData.regime, count: 0, evaluate: false };
			const polarPoints: any[] = [];
			const timelinePoints: any[] = [];
			// Note: now slicing to only last 2000 operations, because turboThreshold is set to 2000 points
			for (const op of regimeData.operations.slice(-2000)) {
				polarPoints.push({ x: DvsUtilities.AddDegreeOffset(op.phase, this.selectedVShaft.rotate), y: op.mag, time: op.ts });
				timelinePoints.push({ x: op.ts * 1000, y: op.mag });
			}
			this.allPolarPoints.push(polarPoints);
			polarSeries.push({
				name: regimeLookup.name,
				type: "scatter",
				// Increase the amount of possible datapoints (1000 by default)
				turboThreshold: 2000,
				data: polarPoints.slice(), // Copy array because otherwise highcharts will mess it up later
				point: {
					events: {
						mouseOver: (evt) => {
							const pointTime = (evt.target as any).time;
							this.timelineChart.SelectPoint(thisRegime, "x", pointTime * 1000);
						},
					},
				},
				events: {
					mouseOut: () => {
						this.timelineChart.SelectPoint();
					},
				},
			});
			timelineSeries.push({
				name: regimeLookup.name,
				type: "line",
				turboThreshold: 2000,
				data: timelinePoints,
				point: {
					events: {
						mouseOver: (evt) => {
							const pointX = (evt.target as any).x;
							this.polarChart.SelectPoint(thisRegime, "time", pointX / 1000);
						},
					},
				},
				events: {
					mouseOut: () => {
						this.polarChart.SelectPoint();
					},
				},
			});
			regimeIndex++;
		}
		this.polarOptions = this.charting.RTBLineDefaults(polarSeries, this.fleet ? this.fleet.time_offset_hours : 0);
		this.timelineOptions = this.charting.RTBHistoryTimelineDefaults(timelineSeries, "Magnitude (IPS)", this.fleet ? this.fleet.time_offset_hours : 0, this.adjustments, this.TimelineZoom, this);
		// Hokey delay to ensure both charts are loaded before we start crosstalk
		setTimeout(() => {
			this.timelineChart.SetXZoom(this.timelineMin, this.timelineMax, false);
		}, 100);

	}

	private TimelineZoom(evt: Highcharts.Axis) {
		this.timelineMin = evt.min;
		this.timelineMax = evt.max;
		for (let i = 0; i < this.allPolarPoints.length; i++) {
			const filtered = this.allPolarPoints[i].filter(item => {
				const time = item.time * 1000;
				if (evt.min !== undefined) {
					if (time < evt.min) {
						return false;
					}
				}
				if (evt.max !== undefined) {
					if (time > evt.max) {
						return false;
					}
				}
				return true;
			});
			this.polarChart.SetSeriesData(i, filtered);
		}
	}

	async updateAdjustments() {
		const result = await this.api.GetAdjustmentHistory(this.asset.asset_id, this.rotor.rotorid);
		this.adjustments = result;
		this.blades = this.rotor.blade.map(item => item.name).join(", ");
	}

	CancelAdjustment() {
		this.archive = undefined;
		this.userNote = undefined;
	}

	async UploadAdjustment() {
		// We have changed to just storing a big text chunk, with adjdef separated by |
		const concatenated = this.archive.adjustments.map(row => row.every(col => col === 0) ? "" : row.join(",")).join("|");
		await this.api.UploadAdjustment(this.asset.asset_id, this.rotor.rotorid, this.archive.operation_id, concatenated, this.userNote);
		await this.updateAdjustments();
		this.timelineOptions = undefined;
		await this.regimesChange();
		this.archive = undefined;
		this.userNote = undefined;
	}

	async DeleteAdjustment(id: number) {
		await this.api.DeleteAdjustment(this.asset.asset_id, id);
		await this.updateAdjustments();
		this.timelineOptions = undefined;
		await this.regimesChange();
	}
}

export interface AdjustmentArchive {
	operation_id: number;
	operation_start: Date;
	adjustments: number[][];
}
